/**
 * Responsive image helpers for public render: `srcset` from stored `media.variants`
 * (see tenant-dashboard `optimize.ts` — each tier has `url` + `width`) and a
 * conservative `sizes` string from layout width hints (CDN cache headers are infra).
 */

import { resolveDeliveryVariantRow } from "./imageDeliveryVariant";

export type VariantSrcSetResult = {
	/** Fallback `<img src>` — largest tier for browsers without `srcset`. */
	src: string;
	/** Comma-separated `url 400w` list, or null when only one width is available. */
	srcSet: string | null;
};

function isVariantRow(v: unknown): v is { url?: unknown; width?: unknown } {
	return typeof v === "object" && v !== null;
}

/** Looks up `width` / `height` on the variant row whose `url` matches `srcUrl` (exact trim match). */
export function intrinsicDimensionsForVariantSrc(
	variants: unknown,
	srcUrl: string
): { width: number; height: number } | undefined {
	const target = typeof srcUrl === "string" ? srcUrl.trim() : "";
	if (!(target && variants && typeof variants === "object")) {
		return undefined;
	}
	for (const v of Object.values(variants as Record<string, unknown>)) {
		if (!isVariantRow(v)) {
			continue;
		}
		const o = v as Record<string, unknown>;
		const url = typeof o.url === "string" ? o.url.trim() : "";
		if (url !== target) {
			continue;
		}
		const w =
			typeof o.width === "number" && o.width > 0 ? Math.floor(o.width) : 0;
		const h =
			typeof o.height === "number" && o.height > 0 ? Math.floor(o.height) : 0;
		if (w > 0 && h > 0) {
			return { width: w, height: h };
		}
		return undefined;
	}
	return undefined;
}

function intrinsicFromVariantRow(
	row: unknown
): { width: number; height: number } | undefined {
	if (!row || typeof row !== "object") {
		return undefined;
	}
	const o = row as Record<string, unknown>;
	const w =
		typeof o.width === "number" && o.width > 0 ? Math.floor(o.width) : 0;
	const h =
		typeof o.height === "number" && o.height > 0 ? Math.floor(o.height) : 0;
	if (w > 0 && h > 0) {
		return { width: w, height: h };
	}
	return undefined;
}

/**
 * Build `src` + optional `srcSet` (`w` descriptors) from `Media.variants` JSON.
 * Ignores entries without a positive numeric `width` (e.g. SVG `original`).
 */
export function buildMediaVariantSrcSet(
	variants: unknown
): VariantSrcSetResult | null {
	if (!(variants && typeof variants === "object")) {
		return null;
	}
	const byWidth = new Map<number, string>();
	for (const v of Object.values(variants as Record<string, unknown>)) {
		if (!isVariantRow(v)) {
			continue;
		}
		const url = typeof v.url === "string" && v.url.trim() ? v.url.trim() : "";
		const w = typeof v.width === "number" && v.width > 0 ? v.width : null;
		if (!(url && w)) {
			continue;
		}
		if (!byWidth.has(w)) {
			byWidth.set(w, url);
		}
	}
	if (byWidth.size === 0) {
		return null;
	}
	const sorted = [...byWidth.entries()].sort((a, b) => a[0] - b[0]);
	const src = sorted[sorted.length - 1]?.[1];
	if (!src) {
		return null;
	}
	if (sorted.length === 1) {
		return { src, srcSet: null };
	}
	const srcSet = sorted.map(([w, u]) => `${u} ${w}w`).join(", ");
	return { src, srcSet };
}

/**
 * Like {@link buildMediaVariantSrcSet} but only includes variants with `width <= maxWidthPx`.
 * Used when a layout-slot derivative caps the largest file the browser should consider.
 */
export function buildMediaVariantSrcSetMaxWidth(
	variants: unknown,
	maxWidthPx: number
): VariantSrcSetResult | null {
	const cap =
		typeof maxWidthPx === "number" &&
		Number.isFinite(maxWidthPx) &&
		maxWidthPx > 0
			? Math.floor(maxWidthPx)
			: 0;
	if (!(cap > 0 && variants && typeof variants === "object")) {
		return null;
	}
	const byWidth = new Map<number, string>();
	for (const v of Object.values(variants as Record<string, unknown>)) {
		if (!isVariantRow(v)) {
			continue;
		}
		const url = typeof v.url === "string" && v.url.trim() ? v.url.trim() : "";
		const w = typeof v.width === "number" && v.width > 0 ? v.width : null;
		if (!(url && w && w <= cap)) {
			continue;
		}
		if (!byWidth.has(w)) {
			byWidth.set(w, url);
		}
	}
	if (byWidth.size === 0) {
		return null;
	}
	const sorted = [...byWidth.entries()].sort((a, b) => a[0] - b[0]);
	const src = sorted[sorted.length - 1]?.[1];
	if (!src) {
		return null;
	}
	if (sorted.length === 1) {
		return { src, srcSet: null };
	}
	const srcSet = sorted.map(([w, u]) => `${u} ${w}w`).join(", ");
	return { src, srcSet };
}

export type ImageSlotDeliveryPrefs = {
	/** Author preset (`small` / `medium` / `large`) or custom variant key; `auto` = unset. */
	deliveryVariant?: string;
	slotVariantKey?: string;
	slotVariantRasterPx?: number;
};

/**
 * Picks `src` / `srcset` for a builder image after media resolution. When a slot
 * variant key is set, caps the candidate list so the browser prefers files at or
 * below the slot raster width (retina target from the editor).
 */
/** Native dimensions for builder placeholder SVG data URLs (16:9). */
export const IMAGE_PLACEHOLDER_INTRINSIC = {
	width: 800,
	height: 450,
} as const;

/** HTML `width` / `height` when both intrinsic dimensions are known (CLS). */
export function imgElementIntrinsicProps(
	width?: number,
	height?: number
): { width?: number; height?: number } {
	if (
		typeof width === "number" &&
		Number.isFinite(width) &&
		width > 0 &&
		typeof height === "number" &&
		Number.isFinite(height) &&
		height > 0
	) {
		return {
			width: Math.round(width),
			height: Math.round(height),
		};
	}
	return {};
}

function positiveInt(value: unknown): number | undefined {
	if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
		return undefined;
	}
	return Math.floor(value);
}

function scaleMediaIntrinsicToWidth(
	mediaWidth: number,
	mediaHeight: number,
	targetWidth: number
): { width: number; height: number } {
	const w = Math.max(1, Math.floor(targetWidth));
	const h = Math.max(
		1,
		Math.round((mediaHeight * targetWidth) / Math.max(1, mediaWidth))
	);
	return { width: w, height: h };
}

function variantWidthForUrl(
	variants: unknown,
	srcUrl: string
): number | undefined {
	const target = srcUrl.trim();
	if (!(target && variants && typeof variants === "object")) {
		return undefined;
	}
	for (const v of Object.values(variants as Record<string, unknown>)) {
		if (!isVariantRow(v)) {
			continue;
		}
		const url = typeof v.url === "string" ? v.url.trim() : "";
		if (url === target) {
			return typeof v.width === "number" && v.width > 0
				? Math.floor(v.width)
				: undefined;
		}
	}
	return undefined;
}

/**
 * Best-effort native dimensions for public `<img width height>` (CLS).
 * Prefers variant rows for `srcUrl`, then any variant with both axes, then
 * `media.width` / `media.height` (scaled to the delivered variant width).
 */
export function resolveMediaIntrinsicDimensions(params: {
	variants: unknown;
	srcUrl?: string;
	mediaWidth?: number | null;
	mediaHeight?: number | null;
}): { width: number; height: number } | undefined {
	const srcUrl = typeof params.srcUrl === "string" ? params.srcUrl.trim() : "";
	if (srcUrl) {
		const fromSrc = intrinsicDimensionsForVariantSrc(params.variants, srcUrl);
		if (fromSrc) {
			return fromSrc;
		}
	}
	if (params.variants && typeof params.variants === "object") {
		for (const v of Object.values(params.variants as Record<string, unknown>)) {
			const dims = intrinsicFromVariantRow(v);
			if (dims) {
				return dims;
			}
		}
	}
	const mediaW = positiveInt(params.mediaWidth);
	const mediaH = positiveInt(params.mediaHeight);
	if (mediaW && mediaH) {
		if (srcUrl) {
			const variantW = variantWidthForUrl(params.variants, srcUrl);
			if (variantW && variantW !== mediaW) {
				return scaleMediaIntrinsicToWidth(mediaW, mediaH, variantW);
			}
		}
		return { width: mediaW, height: mediaH };
	}
	return undefined;
}

function withDeliveryIntrinsicDimensions(
	delivery: ImageMediaDelivery,
	options: {
		variants: unknown;
		mediaWidth?: number | null;
		mediaHeight?: number | null;
	}
): ImageMediaDelivery {
	if (
		typeof delivery.intrinsicWidth === "number" &&
		delivery.intrinsicWidth > 0 &&
		typeof delivery.intrinsicHeight === "number" &&
		delivery.intrinsicHeight > 0
	) {
		return delivery;
	}
	const dims = resolveMediaIntrinsicDimensions({
		variants: options.variants,
		srcUrl: delivery.src,
		mediaWidth: options.mediaWidth,
		mediaHeight: options.mediaHeight,
	});
	if (!dims) {
		return delivery;
	}
	return {
		...delivery,
		intrinsicWidth: dims.width,
		intrinsicHeight: dims.height,
	};
}

export type ImageMediaDelivery = {
	src: string;
	srcSet?: string;
	/** For `<img width height>` (CLS) when known from `media.variants`. */
	intrinsicWidth?: number;
	intrinsicHeight?: number;
};

export function buildImageMediaDelivery(
	variants: unknown,
	resolvedFallback: { url: string; srcSet?: string },
	slot: ImageSlotDeliveryPrefs | undefined,
	mediaDimensions?: { width?: number | null; height?: number | null }
): ImageMediaDelivery {
	const intrinsicOpts = {
		variants,
		mediaWidth: mediaDimensions?.width,
		mediaHeight: mediaDimensions?.height,
	};

	const key =
		typeof slot?.slotVariantKey === "string" ? slot.slotVariantKey.trim() : "";
	if (key && variants && typeof variants === "object") {
		const map = variants as Record<string, unknown>;
		let row: unknown = map[key];
		if (
			!(
				row &&
				typeof row === "object" &&
				typeof (row as { url?: unknown }).url === "string"
			)
		) {
			const canonical = map.pb_slot;
			if (
				canonical &&
				typeof canonical === "object" &&
				typeof (canonical as { url?: unknown }).url === "string"
			) {
				row = canonical;
			} else {
				row = undefined;
			}
		}
		if (
			row &&
			typeof row === "object" &&
			typeof (row as { url?: unknown }).url === "string"
		) {
			const slotUrl = (row as { url: string }).url.trim();
			const slotW =
				typeof (row as { width?: unknown }).width === "number" &&
				(row as { width: number }).width > 0
					? Math.floor((row as { width: number }).width)
					: 0;
			const raster =
				typeof slot?.slotVariantRasterPx === "number" &&
				Number.isFinite(slot.slotVariantRasterPx) &&
				slot.slotVariantRasterPx > 0
					? Math.floor(slot.slotVariantRasterPx)
					: slotW > 0
						? slotW
						: 0;
			const cap = raster > 0 ? raster : 4096;
			const capped = buildMediaVariantSrcSetMaxWidth(variants, cap);
			if (capped) {
				const dims = intrinsicDimensionsForVariantSrc(variants, capped.src);
				return withDeliveryIntrinsicDimensions(
					{
						src: capped.src,
						...(capped.srcSet ? { srcSet: capped.srcSet } : {}),
						...(dims
							? {
									intrinsicWidth: dims.width,
									intrinsicHeight: dims.height,
								}
							: {}),
					},
					intrinsicOpts
				);
			}
			if (slotUrl) {
				const dims = intrinsicFromVariantRow(row);
				return withDeliveryIntrinsicDimensions(
					{
						src: slotUrl,
						...(dims
							? {
									intrinsicWidth: dims.width,
									intrinsicHeight: dims.height,
								}
							: {}),
					},
					intrinsicOpts
				);
			}
		}
	}
	const deliveryVariant =
		typeof slot?.deliveryVariant === "string"
			? slot.deliveryVariant.trim()
			: "";
	if (deliveryVariant) {
		const preset = resolveDeliveryVariantRow(variants, deliveryVariant);
		if (preset) {
			const cap = preset.width > 0 ? preset.width : 0;
			if (cap > 0) {
				const capped = buildMediaVariantSrcSetMaxWidth(variants, cap);
				if (capped) {
					const dims = intrinsicDimensionsForVariantSrc(variants, capped.src);
					return withDeliveryIntrinsicDimensions(
						{
							src: capped.src,
							...(capped.srcSet ? { srcSet: capped.srcSet } : {}),
							...(dims
								? {
										intrinsicWidth: dims.width,
										intrinsicHeight: dims.height,
									}
								: {}),
						},
						intrinsicOpts
					);
				}
			}
			const dims = intrinsicDimensionsForVariantSrc(variants, preset.url);
			return withDeliveryIntrinsicDimensions(
				{
					src: preset.url,
					...(dims
						? {
								intrinsicWidth: dims.width,
								intrinsicHeight: dims.height,
							}
						: {}),
				},
				intrinsicOpts
			);
		}
	}

	const dimsFallback = intrinsicDimensionsForVariantSrc(
		variants,
		resolvedFallback.url
	);
	return withDeliveryIntrinsicDimensions(
		{
			src: resolvedFallback.url,
			...(resolvedFallback.srcSet ? { srcSet: resolvedFallback.srcSet } : {}),
			...(dimsFallback
				? {
						intrinsicWidth: dimsFallback.width,
						intrinsicHeight: dimsFallback.height,
					}
				: {}),
		},
		intrinsicOpts
	);
}

function stringish(v: unknown): string | undefined {
	if (typeof v !== "string") {
		return undefined;
	}
	const t = v.trim();
	return t === "" ? undefined : t;
}

/** When layout gives no pixel bounds, cap assumed width so `srcset` picks a smaller tier than `large` in typical content columns (not full-bleed heroes). */
export const IMAGE_SIZES_DEFAULT_CAP_PX = 640;

/**
 * Derives a `sizes` attribute from layout hints so the browser can pick a
 * reasonable variant when `srcset` uses `w` descriptors.
 *
 * Note: `width: N%` is **not** converted to `vw` (percent is of the parent, not
 * the viewport). Without a `px` hint we use a capped default; full-bleed images
 * should set an explicit `max-width` (e.g. `100vw` / `1200px`) in builder styles.
 */
export function imageSizesAttributeFromLayout(style: {
	maxWidth?: unknown;
	width?: unknown;
}): string {
	const maxW = stringish(style.maxWidth);
	const width = stringish(style.width);
	const pxMax = maxW ? /^(\d+(?:\.\d+)?)px$/i.exec(maxW) : null;
	if (pxMax) {
		return `(max-width: 767px) 100vw, min(100vw, ${pxMax[1]}px)`;
	}
	const pxW = width ? /^(\d+(?:\.\d+)?)px$/i.exec(width) : null;
	if (pxW) {
		return `(max-width: 767px) 100vw, min(100vw, ${pxW[1]}px)`;
	}
	// Full-viewport intent (explicit) — allow largest `srcset` candidate when appropriate.
	if (
		width === "100vw" ||
		width === "100dvw" ||
		maxW === "100vw" ||
		maxW === "100dvw" ||
		maxW === "none"
	) {
		return "100vw";
	}
	return `(max-width: 767px) 100vw, min(100vw, ${IMAGE_SIZES_DEFAULT_CAP_PX}px)`;
}
